import { useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import {
  ChatViewType,
  ChatViewTypeContext,
  SideBarExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context';
import { ActiveChatSessionContext } from '@trinity/chat-components/components/Chat/context';
import { ChatTabsContext } from '@trinity/chat-components/components/Chat.History/context';
import { useLoadChatInstance } from '@trinity/chat-components/components/Chat.History/hooks';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ChatStateDispatchContext } from '../Chat.State/context';

export const ChatTabs = () => {
  const { index, items: tabs } = useContext(ChatTabsContext);
  const session = useContext(ActiveChatSessionContext);
  const chatViewType = useContext(ChatViewTypeContext);
  const [sidebarExpanded] = useContext(SideBarExpandedContext) || [true];
  const loadInstance = useLoadChatInstance();
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifierNullSafe();

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: number) => {
      dispatch({
        type: 'chat-tabs-index-changed',
        payload: { index: newValue },
      });
    },
    [dispatch]
  );

  const activateTab = useCallback(
    (tabItem: TabData['item']) => {
      if (tabItem.identifier === session.identifier) {
        return;
      }

      if (tabItem.id == null) {
        dispatch({
          type: 'unsaved-chat-tab-loaded',
          payload: {
            chatInstanceIdentifier: tabItem.identifier,
          },
        });
        return;
      }

      loadInstance(tabItem.identifier);
    },
    [dispatch, loadInstance, session.identifier]
  );

  const handleClickTab = useCallback(
    (_: React.SyntheticEvent, tab: TabData) => {
      dispatch({
        type: 'chat-tabs-index-changed',
        payload: { index: tab.index },
      });
      activateTab(tab.item);
    },
    [activateTab, dispatch]
  );

  const handleCloseTab = useCallback(
    (e: React.SyntheticEvent, tab: TabData) => {
      e.stopPropagation();

      const nextTabs = tabs.filter((_, i) => i !== tab.index).map((x, i) => ({ ...x, index: i }));

      if (nextTabs.length === 0) {
        dispatch({
          type: 'batch-actions',
          payload: {
            actions: [
              {
                type: 'chat-tabs-items-changed',
                payload: {
                  items: [],
                },
              },
              {
                type: 'new-chat-started',
                payload: {
                  context: session.context,
                  brandIdentifier,
                },
              },
            ],
          },
        });

        return;
      }

      dispatch({
        type: 'chat-tabs-items-changed',
        payload: {
          items: nextTabs.map((m) => m.identifier),
        },
      });

      const activeTab = tabs[index];
      const tabWasActive = activeTab?.identifier === tab.item.identifier;

      if (!tabWasActive && activeTab) {
        const nextIndex = nextTabs.findIndex((x) => x.identifier === activeTab?.identifier);

        dispatch({
          type: 'chat-tabs-index-changed',
          payload: { index: nextIndex },
        });
      }

      if (tabWasActive) {
        const nextActive = nextTabs[tab.index - 1] || nextTabs[tab.index + 1];
        const nextIndex = nextTabs.findIndex((x) => x.identifier === nextActive?.identifier);
        const resolvedIndex = Number.isInteger(nextIndex) ? nextIndex : -1;

        dispatch({
          type: 'chat-tabs-index-changed',
          payload: { index: resolvedIndex },
        });

        if (resolvedIndex > -1) {
          const nextTab = nextTabs[resolvedIndex];
          if (nextTab) {
            activateTab(nextTab);
          }
        }
      }
    },
    [activateTab, tabs, index, session.context, dispatch, brandIdentifier]
  );

  const isModal = chatViewType === ChatViewType.Modal;
  let maxVisibleTabs: number;
  if (isModal) {
    maxVisibleTabs = sidebarExpanded ? 1 : 2;
  } else {
    maxVisibleTabs = 5;
  }

  const filteredTabs = tabs.map((item, tabIndex) => ({ item, index: tabIndex }));
  const showCloseButton = tabs.length > 1;

  const needsDropdown = filteredTabs.length > maxVisibleTabs;
  const hasTabsToNavigate = filteredTabs.length > 1;
  const visibleTabs = needsDropdown ? filteredTabs.slice(0, maxVisibleTabs) : filteredTabs;
  const activeVisibleTabIndex = visibleTabs.findIndex((tab) => tab.index === index);

  let tabValue: number | false;
  if (index === -1) {
    tabValue = false;
  } else if (activeVisibleTabIndex > -1) {
    tabValue = index;
  } else {
    tabValue = false;
  }

  return (
    <>
      <TabsStyled
        value={tabValue}
        variant={needsDropdown ? 'standard' : 'scrollable'}
        scrollButtons={!needsDropdown && hasTabsToNavigate ? 'auto' : false}
        allowScrollButtonsMobile={!needsDropdown && hasTabsToNavigate}
        onChange={handleChange}
        sx={{
          ...(needsDropdown && {
            '& .MuiTabs-flexContainer': {
              minWidth: 'fit-content',
            },
          }),
        }}
      >
        {visibleTabs.map(({ item, index: tabIndex }) => (
          <ChatTab
            active={index === tabIndex}
            key={item.identifier}
            label={item.name}
            index={tabIndex}
            item={item}
            onClick={handleClickTab}
            onClose={handleCloseTab}
            showCloseButton={showCloseButton}
          />
        ))}
        {visibleTabs.length > 0 && (
          <TabIndicator
            count={visibleTabs.length}
            index={activeVisibleTabIndex > -1 ? activeVisibleTabIndex : 0}
          />
        )}
      </TabsStyled>
      {needsDropdown && (
        <TabDropdown
          items={filteredTabs.slice(maxVisibleTabs).map(({ item, index: absoluteIndex }) => {
            return {
              active: index === absoluteIndex,
              item,
              label: item.name || 'Untitled Chat',
              index: absoluteIndex,
              onClick: handleClickTab,
              onClose: handleCloseTab,
              showCloseButton,
            };
          })}
        />
      )}
    </>
  );
};

ChatTabs.displayName = 'Chat.Tabs';

type ChatTabProps = {
  active?: boolean;
  index: number;
  label?: string | null;
  item: { id: number | null; identifier: string; name: string };
  onClick: (e: React.SyntheticEvent, tab: TabData) => void;
  onClose: (e: React.SyntheticEvent, tab: TabData) => void;
  showCloseButton?: boolean;
};

const ChatTab = ({ label, item, showCloseButton = true, ...props }: ChatTabProps) => {
  const tabLabel = label ?? 'Untitled Chat';

  return (
    <TabStyled
      value={props.index}
      active={props.active}
      disableRipple
      onClick={(e) => props.onClick(e, { item, index: props.index })}
      iconPosition="end"
      icon={
        showCloseButton ? (
          <TabCloseButton
            aria-label={`Close ${tabLabel} tab`}
            onClick={(e) => {
              e.stopPropagation();
              props.onClose(e, { item, index: props.index });
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                props.onClose(e, { item, index: props.index });
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </TabCloseButton>
        ) : undefined
      }
      label={<TabLabel title={tabLabel}>{tabLabel}</TabLabel>}
    />
  );
};

type TabData = {
  index: number;
  item: { id: number | null; identifier: string; name: string };
};

const TabsStyled = styled(Tabs)(({ theme }) => ({
  minHeight: 43,
  maxWidth: 940,
  height: 43,
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.sentiment.primary.main,
  },
}));

type TabProps = {
  active?: boolean;
};

const TAB_WIDTH = 180;

const TabStyled = styled(Tab, { shouldForwardProp: (prop) => prop !== 'active' })<TabProps>(
  ({ active, theme }) => ({
    padding: '0 16px',
    height: 43,
    minHeight: 43,
    maxHeight: 43,
    color: active ? theme.palette.sentiment.primary.main : theme.palette.black.main,
    opacity: active ? 1 : undefined,
    fontFamily: theme.fonts.semiBold,
    width: TAB_WIDTH,
    maxWidth: TAB_WIDTH,
    minWidth: TAB_WIDTH,
    borderRight: `1px solid ${theme.palette.gray.light4}`,
    borderTopRightRadius: '5px',
    borderTopLeftRadius: '5px',
    borderTop: `1px solid ${theme.palette.gray.light4}`,

    '&:first-of-type': {
      borderLeft: `1px solid ${theme.palette.gray.light4}`,
    },

    '&:hover': {
      transition: 'color 180ms ease-in',
      color: theme.palette.sentiment.primary.main,
    },
  })
);

const TabCloseButton = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 0,
  background: 'transparent',
  color: theme.palette.gray.main,
  borderRadius: '50%',
  cursor: 'pointer',

  '&:hover': {
    transition: 'color 180ms ease-in',
    color: theme.palette.sentiment.primary.main,
  },
}));

const TabLabel = styled.div(({ theme }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontFamily: theme.fonts.semiBold,
  fontSize: 13,
}));

const TabIndicator = (props: { count: number; index: number }) => {
  const left = TAB_WIDTH * props.index;
  const width = TAB_WIDTH;

  return <Indicator style={{ left, width }} />;
};

const Indicator = styled.div(({ theme }) => ({
  position: 'absolute',
  height: '2px',
  backgroundColor: theme.palette.sentiment.primary.main,
  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  zIndex: 1,
  bottom: 0,
}));

const TabDropdown = (props: { items: ChatTabProps[] }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? 'spring-popper' : undefined;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div>
        <DropdownMenuButton type="button" aria-describedby={id} onClick={handleClick}>
          <ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
        </DropdownMenuButton>
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          disablePortal={false}
          style={{ zIndex: 1300 }}
        >
          <DropdownMenu items={props.items} onClose={() => setOpen(false)} />
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

const DropdownMenuButton = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 43,
  width: 50,
  color: theme.palette.black.main,
}));

const DropdownMenuItem = (props: ChatTabProps) => {
  const { label, item, index, onClick, onClose, showCloseButton = true } = props;

  return (
    <DropdownItemStyled active={props.active} disableRipple onClick={(e) => onClick(e, { item, index })}>
      <Typography variant="inherit" noWrap>
        {label}
      </Typography>
      {showCloseButton && (
        <ListItemIcon>
          <CloseIcon fontSize="small" onClick={(e) => onClose(e, { item, index })} />
        </ListItemIcon>
      )}
    </DropdownItemStyled>
  );
};

const DropdownMenu = (props: { items: ChatTabProps[]; onClose: () => void }) => {
  const closeAfterClick = useCallback(
    (fn: ChatTabProps['onClick']) => {
      return (e: React.SyntheticEvent, tab: TabData) => {
        fn(e, tab);
        props.onClose();
      };
    },
    [props]
  );

  const closeIfEmpty = useCallback(
    (fn: ChatTabProps['onClose']) => {
      return (e: React.SyntheticEvent, tab: TabData) => {
        fn(e, tab);
        if (props.items.length === 1) {
          props.onClose();
        }
      };
    },
    [props]
  );

  return (
    <Paper
      sx={{
        width: 230,
        backgroundColor: 'white',
        border: '1px solid #ccc',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      }}
    >
      <MenuList>
        {props.items.map((x) => (
          <DropdownMenuItem
            active={x.active}
            key={x.index}
            label={x.label}
            item={x.item}
            index={x.index}
            onClick={closeAfterClick(x.onClick)}
            onClose={closeIfEmpty(x.onClose)}
            showCloseButton={x.showCloseButton}
          />
        ))}
      </MenuList>
    </Paper>
  );
};

const DropdownItemStyled = styled(MenuItem)<{ active?: boolean }>(({ active, theme }) => ({
  justifyContent: 'space-between',
  backgroundColor: (() => {
    if (!active) return undefined;
    if (theme.isTrinityThemed) return theme.palette.sentiment.primary.light;
    return theme.palette.btnPrimary.bg;
  })(),

  '&:hover': {
    backgroundColor: theme.isTrinityThemed
      ? theme.palette.sentiment.primary.light
      : theme.palette.btnPrimary.bg,
  },
}));
